import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
  Chip
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon
} from '@mui/icons-material';
import CallNowButton from '../../../components/shared/CallNowButton';
import { getGoogleReviews } from '../../../services/googleReviewsService';

// LCP: Use public path for preload; img with fetchpriority improves LCP
const HERO_IMAGE_SRC = `${process.env.PUBLIC_URL || ''}/main.webp`;

const MOBILE_TRUST_POINTS = [
  { title: 'Warranty Included', subline: '12,000 miles or 12 months' },
  { title: 'Licensed & Insured', subline: 'BAR-ARD License #304194' },
  { title: 'Upfront Pricing' },
  { title: 'Fast Scheduling' }
];

const MobileTrustCell = ({ title, subline, checkColor }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      py: 0.75,
      pr: 1
    }}
  >
    <CheckIcon
      sx={{
        fontSize: 20,
        color: checkColor,
        flexShrink: 0,
        mt: subline ? 0.25 : 0
      }}
    />
    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.3,
          color: 'white',
          textShadow: '0 1px 3px rgba(0,0,0,0.8)'
        }}
      >
        {title}
      </Typography>
      {subline && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            lineHeight: 1.3,
            color: alpha('#fff', 0.9),
            textShadow: '0 1px 3px rgba(0,0,0,0.8)'
          }}
        >
          {subline}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * Hero Section - Modern, clean hero with centered content
 * LCP element: hero img (converted from CSS background for preload/fetchpriority)
 */
const HeroSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [googleRating, setGoogleRating] = useState(4.9);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    const fetchReviews = () => {
      getGoogleReviews()
        .then((data) => {
          if (data?.rating) setGoogleRating(data.rating);
          if (data?.user_ratings_total) setTotalReviews(data.user_ratings_total);
        })
        .catch(() => {});
    };
    let idleId;
    let fetchTimerId;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(fetchReviews, { timeout: 2000 });
    } else {
      fetchTimerId = setTimeout(fetchReviews, 300);
    }
    return () => {
      if (idleId) window.cancelIdleCallback(idleId);
      if (fetchTimerId) clearTimeout(fetchTimerId);
    };
  }, []);

  const QUOTE_ROUTE = '/quote'; // TODO: Verify route
  const trustCheckColor = theme.palette.success?.light || '#34d399';
  const heroTextShadow = '0 1px 3px rgba(0,0,0,0.85), 0 4px 16px rgba(0,0,0,0.5)';

  return (
    <Box
      sx={{
        position: 'relative',
        color: 'white',
        pt: { xs: 10, sm: 12, md: 16 },
        pb: { xs: 10, sm: 12, md: 16 },
        overflow: 'hidden',
        minHeight: { xs: 660, sm: 680, md: 700 },
        display: 'flex',
        alignItems: 'center',
        ...(isMobile && {
          width: '100vw',
          maxWidth: '100vw',
          left: '50%',
          marginLeft: '-50vw',
          boxSizing: 'border-box'
        })
      }}
    >
      {/* LCP: img with fetchpriority + dimensions (no loading="lazy") */}
      <Box
        component="img"
        src={HERO_IMAGE_SRC}
        alt=""
        fetchPriority="high"
        width={1200}
        height={700}
        decoding="async"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center top',
          zIndex: 0
        }}
      />
      {/* Gradient overlays */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse 100% 70% at 50% 20%, rgba(59, 130, 246, 0.22) 0%, transparent 55%),
            radial-gradient(ellipse 80% 50% at 50% 60%, rgba(245, 158, 11, 0.06) 0%, transparent 50%),
            linear-gradient(180deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.75) 50%, rgba(12, 25, 41, 0.9) 100%),
            linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)
          `,
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      <Container
        maxWidth="lg"
        disableGutters={isMobile}
        sx={{
          position: 'relative',
          zIndex: 2,
          px: isMobile ? 2 : undefined
        }}
      >
        <Box sx={{
          maxWidth: { xs: '100%', md: '800px' },
          mx: 'auto',
          textAlign: { xs: 'center', md: 'center' }
        }}>
          <Chip
            label="Professional Mobile Auto Repair"
            sx={{
              mb: 3,
              background: alpha(theme.palette.warning?.main || '#f59e0b', 0.35),
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha('#fff', 0.4)}`
            }}
          />

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: 40, md: 58 },
              lineHeight: 1.05,
              letterSpacing: '-0.02em'
            }}
          >
            Mobile Auto Repair at Your Location
          </Typography>

          {/* Google rating — live API, same as quote page + 5 stars */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2.5,
              flexWrap: 'wrap',
              textShadow: heroTextShadow
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              aria-hidden
              sx={{ width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 }, flexShrink: 0 }}
            >
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.15, lineHeight: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <StarIcon
                  key={i}
                  sx={{ fontSize: { xs: 18, sm: 22 }, color: '#fbbf24' }}
                />
              ))}
            </Box>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '1.125rem', sm: '1.375rem', md: '1.5rem' }
              }}
            >
              {googleRating} Rated
            </Typography>
            {totalReviews > 0 && (
              <Typography
                sx={{
                  color: alpha('#fff', 0.9),
                  fontWeight: 500,
                  fontSize: { xs: '0.9375rem', sm: '1.0625rem' }
                }}
              >
                ({totalReviews.toLocaleString()} {totalReviews === 1 ? 'review' : 'reviews'})
              </Typography>
            )}
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2, mt: { xs: 2, sm: 3, md: 4 }, justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(QUOTE_ROUTE)}
              sx={{
                height: { xs: 50, md: 52 },
                px: { xs: 4, md: 5 },
                fontSize: { xs: 15, md: 16 },
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                color: 'white',
                boxShadow: `0 8px 24px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                textTransform: 'none',
                transition: 'all 0.3s ease',
                '@media (hover: hover)': {
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`
                  }
                },
                '@media (hover: none)': {
                  '&:active': {
                    transform: 'scale(0.97)',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.35)}`
                  }
                }
              }}
            >
              Get Instant Quote
            </Button>

            <CallNowButton
              sx={{
                height: { xs: 50, md: 52 },
                px: { xs: 4, md: 5 },
                fontSize: { xs: 15, md: 16 },
                fontWeight: 600,
                borderRadius: 3,
                borderColor: 'white',
                color: 'white',
                borderWidth: 2,
                textTransform: 'none',
                '@media (hover: hover)': {
                  transition: 'all 0.25s ease-out',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.15),
                    borderWidth: 2
                  }
                }
              }}
            />
          </Stack>

          <Typography
            sx={{
              mb: 4,
              opacity: 0.8,
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 400
            }}
          >
            Takes 60 seconds. No obligation.
          </Typography>

          {/* Mobile: 2×2 trust grid — desktop keeps horizontal chips below */}
          {isMobile ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 1.5,
                rowGap: 1,
                width: '100%'
              }}
            >
              {MOBILE_TRUST_POINTS.map((point) => (
                <MobileTrustCell
                  key={point.title}
                  title={point.title}
                  subline={point.subline}
                  checkColor={trustCheckColor}
                />
              ))}
            </Box>
          ) : (
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
                alignItems: 'baseline'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
                <CheckIcon sx={{ fontSize: 20, color: theme.palette.success?.light || '#34d399', mt: 0.25 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Warranty Included
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.9, fontWeight: 400, lineHeight: 1.3 }}>
                    12,000 miles or 12 months
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
                <CheckIcon sx={{ fontSize: 20, color: theme.palette.success?.light || '#34d399', mt: 0.25 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Licensed & Insured
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.9, fontWeight: 400, lineHeight: 1.3 }}>
                    BAR-ARD License #304194
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, whiteSpace: 'nowrap' }}>
                <CheckIcon sx={{ fontSize: 20, color: theme.palette.success?.light || '#34d399' }} />
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Upfront Pricing</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, whiteSpace: 'nowrap' }}>
                <CheckIcon sx={{ fontSize: 20, color: theme.palette.success?.light || '#34d399' }} />
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Fast Scheduling</Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
